import { action, batch } from '@reatom/framework'

import { pipe, Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { positionsDtoUpdatedEvent } from '@root/entities/position/atoms/positions-registry'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'

import { FavoriteUnavailable } from '../model/favorite-unavailable'

import type { TypeHelper } from '@blade/std-lib'
import type { UserFavouritePairsCreatePayload } from '@blade/trade-api-client'
import type { CurrencyAddress } from '@root/entities/currency'
import type { FavoriteId, PairAddress } from '@root/entities/pair'

export const fetchFavoritePairsRequestAction = action(
  async (
    ctx,
    {
      network,
      ...requestParams
    }: TypeHelper.OmitKey<UserFavouritePairsCreatePayload, 'network'> & {
      network: Network.Network
    },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userFavouritePairsCreate, [
        {
          network: Network.toDto(network),
          ...requestParams,
        },
      ]),
    )

    if (Result.isSuccess(result)) {
      batch(ctx, () => {
        for (const it of result.success.data) {
          if ('unavailable' in it) {
            continue
          }
          const userPosition = it.userPosition
          if (userPosition) {
            positionsDtoUpdatedEvent(ctx, userPosition)
          }
        }
      })
    }

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
      Result.map((x) => {
        return {
          data: x.data
            .map((it) => {
              if ('unavailable' in it) {
                return FavoriteUnavailable.fromDto(it)
              }
              return it
            })
            .filter(Boolean),
        }
      }),
    )

    return mappedResult
  },
  'fetchFavoritePairsRequestAction',
)

interface ToggleFavoritePairRequestOptions {
  network: Network.Network
  pair: PairAddress
  baseTokenAddress: CurrencyAddress
  quoteTokenAddress: CurrencyAddress
}

export const toggleFavoritePairRequestAction = action(
  async (ctx, options: ToggleFavoritePairRequestOptions) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userToggleFavouritePairCreate, [
        {
          network: Network.toDto(options.network),
          pairAddress: options.pair,
          baseTokenAddress: options.baseTokenAddress,
          quoteTokenAddress: options.quoteTokenAddress,
        },
      ]),
    )

    return pipe(result, Result.mapError(ApiError.fallbackToUnknown))
  },
  'toggleFavoritePairRequestAction',
)

interface SetFavoritePairsOrderPayload {
  network: Network.Network
  favoritePairsOrder: ReadonlyArray<FavoriteId>
}

export const setFavoritePairsOrderRequestAction = action(
  async (ctx, args: SetFavoritePairsOrderPayload) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userSetFavouritePairsOrderCreate, [
        {
          metas: args.favoritePairsOrder.map((favoriteId, index) => ({
            favoriteId,
            order: index,
          })),
          network: Network.toDto(args.network),
        },
      ]),
    )

    return pipe(result, Result.mapError(ApiError.fallbackToUnknown))
  },
  'setFavoritePairsOrderRequestAction',
)

export const fetchFavoriteMetasRequestAction = action(
  async (ctx, options: { network: Network.Network }) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userFavouritePairsMetaCreate, [
        {
          network: Network.toDto(options.network),
        },
      ]),
    )

    return pipe(result, Result.mapError(ApiError.fallbackToUnknown))
  },
  'fetchFavoriteMetasRequestAction',
)
